import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import api from '@/api';
import type { components } from '@/api/qraft/schema';
import { NotFoundScreen } from '@/common/ErrorScreen';
import { FollowUpNotes } from '@/follow-up/FollowUpNotes';
import { SectionHeading } from '@/follow-up/followUpStyles';
import {
  GroupFollowUpRequestDetail,
  Section,
} from '@/follow-up/GroupFollowUpRequestDetail';
import { formatLongDate } from '~/utils/dateFormat';
import { formatString } from '~/utils/stringFormat';

type RequestDetail = components['schemas']['GroupFollowUpRequestDetail'];

export const Route = createFileRoute(
  '/_app/_dashboard/groups/$groupName/follow-ups/follow-up-requests/view/$requestId',
)({
  component: ViewGroupRequestScreen,
});

/**
 * Subnav 1's request detail.
 *
 * Three of legacy's blocks aren't rendered: the link to the request's follow-up
 * (v3 doesn't record it on the request), the self-request "Message to Study
 * Helper" (a request's creator is always a study helper), and the student's
 * rejection message, which is for a student viewer. Empty message blocks fall
 * back to "No message", as in legacy.
 */
function ViewGroupRequestScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { groupName, requestId } = Route.useParams();

  const { data: groupData } = api.getGroupBySlug.useQuery({
    path: { group_slug: groupName },
  });
  const groupId = groupData?.id;

  const {
    data: detail,
    isLoading,
    isError,
  } = api.getGroupFollowUpRequest.useQuery(
    {
      path: { group_id: groupId ?? 0, request_id: Number(requestId) },
    },
    { enabled: groupId !== undefined },
  );

  const backToSubnav = () =>
    void navigate({
      to: '/groups/$groupName/follow-ups/follow-up-requests/',
      params: { groupName },
    });

  // 404 for a request outside the group's roster, the same screen as an unknown
  // id.
  if (isError) return <NotFoundScreen />;
  if (isLoading || detail === undefined) return null;

  return (
    <GroupFollowUpRequestDetail
      detail={detail}
      onBack={backToSubnav}
      beforeContact={<StudyInfoBlock detail={detail} />}
    >
      {/* The study helper's message to the student. Legacy prints "No
         message" rather than dropping an empty block. */}
      <FollowUpNotes
        title={t('lessons.message-from-study-helper')}
        message={detail.message_for_student || t('wbs.no-message-2')}
      />
      <StatusBlock detail={detail} />
    </GroupFollowUpRequestDetail>
  );
}

/**
 * The student's study info, from the request's stored snapshot, so the detail
 * agrees with the list. Legacy's detail showed live progress instead.
 */
function StudyInfoBlock({ detail }: { detail: RequestDetail }) {
  const { t } = useTranslation();
  const progress = detail.study_progress;

  // Stored as a string, `''` or `'1'`. `'0'` is excluded explicitly because PHP
  // treats it as falsy and JavaScript doesn't.
  const graduated = progress.graduated !== '' && progress.graduated !== '0';

  return (
    <Section>
      <SectionHeading>{t('lessons.student-s-study-info')}</SectionHeading>
      <StudyGrid>
        <Field>
          <FieldLabel>{t('wbs.graduated-2')}</FieldLabel>
          <span>{graduated ? t('wbs.yes') : t('wbs.no')}</span>
        </Field>
        <Field>
          <FieldLabel>{t('wbs.current-progress')}</FieldLabel>
          {/* `study_progress_label` is the snapshot's `current_progress`
             resolved to a title. Legacy prints "no progress" when it's
             empty; that string isn't translated, so "n/a" stands in. */}
          <span>{detail.study_progress_label || t('wbs.n-a-2')}</span>
        </Field>
        <Field>
          <FieldLabel>{t('lessons.average-quiz-score')}</FieldLabel>
          <span>{progress.average_quiz_score}%</span>
        </Field>
        <Field>
          <FieldLabel>{t('lessons.completed-self-paced-courses')}</FieldLabel>
          <span>{progress.completed_self_paced_courses}</span>
        </Field>
      </StudyGrid>
    </Section>
  );
}

/**
 * The status-branched block. `pending`, `adopted` and `in_progress` render
 * none.
 */
function StatusBlock({ detail }: { detail: RequestDetail }) {
  const { t } = useTranslation();

  if (detail.status === 'approved') {
    return (
      <FollowUpNotes
        // Legacy's "when Accepted" heading. The pending detail's "if Accepted"
        // is a different catalogue string.
        title={t('wbs.your-note-to-the-4')}
        message={
          detail.message_for_follow_up_coordinator || t('wbs.no-message-2')
        }
      />
    );
  }

  if (detail.status === 'rejected') {
    return (
      <FollowUpNotes
        // "Feedback on for Rejection" (sic) is legacy's own copy.
        title={t('wbs.feedback-on-for-rejection')}
        message={
          detail.message_for_study_helper_reject || t('wbs.no-message-2')
        }
      />
    );
  }

  if (detail.status === 'canceled') {
    return (
      <FollowUpNotes
        title={
          <>
            {t('wbs.reason-for-cancelation')}
            {/* Legacy prints the canceled date unconditionally, but most
               migrated canceled requests have no `canceled_at`, so it's
               shown only when there's a timestamp. */}
            {detail.canceled_at !== null && (
              <CanceledOn>
                {formatString(
                  t('wbs.cancelled-on-s'),
                  formatLongDate(detail.canceled_at),
                )}
              </CanceledOn>
            )}
          </>
        }
        // This block falls back to "n/a" rather than "No message", as legacy's
        // does.
        message={detail.message_for_cancelation || t('wbs.n-a-2')}
      />
    );
  }

  return null;
}

const StudyGrid = styled.div`
  column-gap: 25px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  row-gap: 22px;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div`
  display: grid;
  font-size: 13px;
  gap: 16px;
  grid-template-columns: 190px 1fr;
`;

const FieldLabel = styled.span`
  color: var(--grey-900);
`;

const CanceledOn = styled.small`
  font-size: 13px;
  margin-left: 5px;
`;
